import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter, serverContext } from '@adornis/baseql/server/context.js';
import { runInContext } from '@adornis/baseql/server/server.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import { fileConfig } from '@adornis/file-utils/config.js';
import { AdornisTemplater } from '@adornis/templater/server/index.js';
import { addRoleToUser } from '@adornis/users/db/a-user.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { assignRoleToUser } from '@adornis/users/server/roles.js';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Router } from 'express';
import { mkdir, readFile, writeFile } from 'fs/promises';
import { join } from 'path';
import { switchMap } from 'rxjs';
import { Institution } from '../entities/institution.js';
import { LecturerContract, LecturerContractFile, LecturerContractVariable } from '../entities/LecturerContract.js';
import { getLecturerDataByLecturerID } from '../entities/lecturerData.js';
import { Product } from '../entities/products.js';
import { Teaching } from '../entities/teaching.js';
import { User } from '../entities/user.js';
import { Workshop } from '../entities/workshop.js';
import { Permission, Role } from '../enums/roles.js';
import { notifyLecturerAddedToProduct } from '../notification/lecturer-added-to-product.js';
import { notifyLecturerRegistration } from '../notification/lecturer-registration.js';

export const subscribeTeachingsForLecturer = registerSubscription({
  operationName: 'subscribeTeachingsForLecturer',
  resolve:
    (skip: number, limit: number, lecturerID: string) =>
    <SelSet extends BaseQLSelectionSet<Teaching>>(gqlFields: SelSet) =>
      watchQuery<Teaching>(Teaching._collectionName, { lecturerID }, { skip, limit }),
  type: () => [Teaching],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'lecturerID', type: () => ID },
  ],
});

export const subscribeTeachingsForLecturerAndInstitution = registerSubscription({
  operationName: 'subscribeTeachingsForLecturerAndInstitution',
  resolve:
    (skip: number, limit: number, lecturerID: string, institutionID: string) =>
    <SelSet extends BaseQLSelectionSet<Teaching>>(gqlFields: SelSet) =>
      watchQuery<Teaching>(Teaching._collectionName, { lecturerID }, { projection: { _id: 1 } }).pipe(
        switchMap(async teachings => {
          const rawCol = await getRawCollection(Teaching._collectionName);
          return rawCol
            .aggregate([
              { $match: { _id: { $in: teachings.map(teaching => teaching._id) } } },
              {
                $lookup: {
                  from: Product._collectionName,
                  as: 'products',
                  localField: 'productID',
                  foreignField: '_id',
                },
              },
              { $unwind: '$products' },
              {
                $lookup: {
                  from: Institution._collectionName,
                  as: 'institution',
                  localField: 'products.institutionID',
                  foreignField: '_id',
                },
              },
              { $unwind: '$institution' },
              { $match: { 'institution._id': institutionID } },
              { $skip: skip },
              { $limit: limit },
            ])
            .toArray();
        }),
      ),
  type: () => [Teaching],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'lecturerID', type: () => ID },
    { name: 'institutionID', type: () => ID },
  ],
});

export const subscribeTeachingsForProduct = registerSubscription({
  operationName: 'subscribeTeachingsForProduct',
  resolve:
    (productID: string) =>
    <SelSet extends BaseQLSelectionSet<Teaching>>(gqlFields: SelSet) =>
      watchQuery<Teaching>(Teaching._collectionName, { productID }),
  type: () => [Teaching],
  params: [{ name: 'productID', type: () => ID }],
});

export const getInstitutionsWithContractsForLecturer = registerQuery({
  operationName: 'getInstitutionsWithContractsForLecturer',
  resolve:
    (lecturerID: string) =>
    async <SelSet extends BaseQLSelectionSet<Institution>>(gqlFields: SelSet) => {
      const rawCol = await getRawCollection(Teaching._collectionName);
      const pF = await publishFilter(Teaching._class);
      return rawCol
        .aggregate([
          { $match: { $and: [pF ?? {}, { lecturerID }] } },
          { $lookup: { from: Product._collectionName, as: 'products', localField: 'productID', foreignField: '_id' } },
          { $unwind: '$products' },
          {
            $lookup: {
              from: Institution._collectionName,
              as: 'institution',
              localField: 'products.institutionID',
              foreignField: '_id',
            },
          },
          { $unwind: '$institution' },
          { $group: { _id: '$institution._id', institution: { $first: '$institution' } } },
          { $replaceRoot: { newRoot: '$institution' } },
        ])
        .toArray();
    },
  type: () => [Institution],
  params: [{ name: 'lecturerID', type: () => ID }],
});

export const promoteUserToLecturer = registerMutation({
  operationName: 'promoteUserToLecturer',
  type: () => Int,
  params: [
    { name: 'userID', type: () => String },
    { name: 'institutionID', type: () => String },
  ],
  resolve: (userID: string, institutionID: string) => async () => {
    // check that institutionID is valid
    const institution = await getByID(Institution, institutionID)({});
    if (!institution) throw new Error('institution not found ' + institutionID);

    await addRoleToUser(userID, Role.LECTURER, [institutionID])();
    await notifyLecturerRegistration(userID, institutionID);

    return 0;
  },
});

export const createTeaching = registerMutation({
  operationName: 'createTeaching',
  type: () => String,
  params: [{ name: 'teaching', type: () => Teaching }],
  resolve: (teaching: Teaching) => async () => {
    const product = await getByID(Product, teaching.productID)({ institutionID: 1 });
    if (!product) throw new Error('No product found for ID: ' + teaching.productID);

    const user = await getByID(User, teaching.lecturerID)({ _id: 1, isLecturer: 1, roles: { name: 1 } });
    if (!user) throw new Error('user not found ' + teaching.lecturerID);

    const currentUser = await CurrentUserInfo.getMyself()({ roles: AdornisRoleToContextsWrapper.allFields });
    if (!currentUser?.hasPermission(Permission.EDIT_LECTURERS, product.institutionID)) {
      throw new Error('You dont have enough permission!');
    }

    const userIsGoingToBeLecturer = !user.isLecturer;

    await teaching.create();
    await runInContext(serverContext.sessionID, () => {
      return assignRoleToUser(teaching.lecturerID, Role.LECTURER, [product.institutionID], User);
    });

    if (userIsGoingToBeLecturer) notifyLecturerRegistration(teaching.lecturerID, product.institutionID);
    await notifyLecturerAddedToProduct(teaching.lecturerID, teaching.productID);

    return teaching._id;
  },
});

export const createContractFromTeaching = registerMutation({
  operationName: 'createContractFromTeaching',
  resolve: (teachingID: string) => async () => {
    const teaching = await getByID(
      Teaching,
      teachingID,
    )({
      _id: 1,
      _class: 1,
      contractID: 1,
      productID: 1,
      lecturerID: 1,
      lecturer: {
        _id: 1,
        firstName: 1,
        lastName: 1,
        email: 1,
      },
      contractPrice: 1,
      contractPriceWords: 1,
      signedContractFileID: 1,
    });
    if (!teaching) throw new Error('No teachign found for ID: ' + teachingID);

    const lecturerContract = await getByID(LecturerContract, teaching.contractID)({ _id: 1, _class: 1, fileID: 1 });
    if (!lecturerContract) throw new Error('No linked contract found for teaching: ' + teachingID);

    const file = await getByID(
      LecturerContractFile,
      lecturerContract.fileID,
    )({ _id: 1, _class: 1, fileID: 1, file: { _id: 1, path: 1 } });
    if (!file) throw new Error('No linked contractFile found for lecturerContract: ' + lecturerContract._id);

    const templater = AdornisTemplater.fromFile(file.getFullPath());

    const product = await getByID(Product, teaching.productID)({ _id: 1, _class: 1, timeframeID: 1, name: 1 });
    if (!product) throw new Error('No linked product found for teaching: ' + teachingID);

    const timeframe = await getByID(Timeframe, product.timeframeID)({ name: 1 });
    // if (!timeframe) throw new Error('No linked timeframe found for product: ' + product._id);

    const lecturerData = await getLecturerDataByLecturerID(teaching.lecturerID)({
      billingAddress: {
        streetAndHousenumber: 1,
        zipCity: {
          zip: 1,
          city: 1,
        },
        country: 1,
      },
    });

    templater.render({
      [LecturerContractVariable.Verguetung_Euro]: teaching.contractPrice,
      [LecturerContractVariable.Verguetung_Ausgeschrieben]: teaching.contractPriceWords,
      [LecturerContractVariable.Vorname]: teaching.lecturer?.firstName,
      [LecturerContractVariable.Nachname]: teaching.lecturer?.lastName,
      [LecturerContractVariable.Anschrift]: lecturerData
        ? `${lecturerData.billingAddress?.streetAndHousenumber}\n${lecturerData.billingAddress?.zipCity?.zip} ${lecturerData.billingAddress?.zipCity?.city}\n${lecturerData.billingAddress?.country}`
        : '',
      [LecturerContractVariable.Produkt_Titel]: product.name,
      [LecturerContractVariable.Produkt_Tag]:
        product instanceof Workshop && product.appointments?.[0]?.start
          ? format(product.appointments[0].start.toJSDate(), 'E', { locale: de })
          : '-',
      [LecturerContractVariable.Produkt_Datum]:
        product instanceof Workshop && product.appointments?.[0]?.start
          ? format(product.appointments[0].start.toJSDate(), 'dd.MM.yyyy')
          : '-',
      [LecturerContractVariable.Produkt_Uhrzeit]:
        product instanceof Workshop && product.appointments?.[0]?.start
          ? format(product.appointments[0].start.toJSDate(), 'hh:mm')
          : '-',
      [LecturerContractVariable.Semester]: timeframe?.name ?? '',
      [LecturerContractVariable.Datum]: format(new Date(), 'dd.MM.yyyy'),
    });
    const pdfBuffer = await templater.toPdfBuffer();
    const dirPath = join(fileConfig.get('datadir'), `/filled-contracts`);
    try {
      await mkdir(dirPath);
    } catch (e) {
      if (!(e as Error).message?.includes('already exists')) throw e;
    }
    await writeFile(join(dirPath, `/${teaching._id}.pdf`), pdfBuffer);
    return `/api/teachings/${teachingID}`;
  },
  type: () => String,
  params: [{ name: 'teachingID', type: () => ID }],
});

export const teachingContractRouter = () => {
  const router = Router();
  router.use('/api/teachings/:teachingID', async (req, res, next) => {
    const teachingID = req.params.teachingID;
    if (!teachingID) {
      res.statusCode = 400;
      return res.end('No teaching ID given');
    }

    try {
      const fileBuffer = await readFile(join(fileConfig.get('datadir'), `/filled-contracts/${teachingID}.pdf`));
      if (!fileBuffer) {
        res.statusCode = 400;
        return res.end('No contract found for ID: ' + teachingID);
      }

      res.writeHead(200, { 'content-type': 'application/pdf' });
      res.end(fileBuffer);
    } catch (e) {
      res.statusCode = 400;
      return res.end('No contract found for ID: ' + teachingID);
    }
  });
  return router;
};

export const getProductTeachings = registerQuery({
  operationName: 'getProductTeachings',
  resolve: (productID: string) => async (gqlFields: BaseQLSelectionSet<Teaching>) => {
    const teachingsForProduct = await (await getRawCollection<Teaching>(Teaching._collectionName))
      .find({ productID })
      .toArray();

    return teachingsForProduct;
  },
  type: () => [Teaching],
  params: [{ name: 'productID', type: () => String }],
});
